import { formatPhoneNumberIntl } from 'react-phone-number-input/core'
import { getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/core'

import { phoneMetadata } from './phone-input-config'

export type PhoneCountryCode = CountryCode

const MAX_PHONE_DIGITS = 20

export const normalizePhoneDigits = (value: string | null | undefined) =>
  String(value ?? '')
    .replace(/\D+/g, '')
    .slice(0, MAX_PHONE_DIGITS)

export const phoneDigitsToE164 = (value: string | null | undefined) => {
  const digits = normalizePhoneDigits(value)
  return digits ? `+${digits}` : undefined
}

export const phoneE164ToDigits = (value: string | null | undefined) =>
  normalizePhoneDigits(value)

const parseExplicitInternationalPhone = (value: string) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return null

  if (trimmed.startsWith('+')) {
    return parsePhoneNumberFromString(trimmed, phoneMetadata)
  }

  if (trimmed.startsWith('00')) {
    const digits = normalizePhoneDigits(trimmed)
    if (digits.length <= 2) return null
    return parsePhoneNumberFromString(`+${digits.slice(2)}`, phoneMetadata)
  }

  return null
}

export const normalizePhoneToStoredDigits = (
  value: string | null | undefined,
  defaultCountry: PhoneCountryCode = 'BR'
) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''

  const explicitPhone = parseExplicitInternationalPhone(trimmed)
  if (explicitPhone?.number) {
    return phoneE164ToDigits(explicitPhone.number)
  }

  const digits = normalizePhoneDigits(trimmed)
  if (!digits) return ''

  const parsedAsInternational = parsePhoneNumberFromString(`+${digits}`, phoneMetadata)
  const parsedWithDefaultCountry = parsePhoneNumberFromString(trimmed, defaultCountry, phoneMetadata)

  if (parsedWithDefaultCountry?.number && parsedWithDefaultCountry.isPossible()) {
    return phoneE164ToDigits(parsedWithDefaultCountry.number)
  }

  if (parsedAsInternational?.number && parsedAsInternational.isPossible()) {
    return phoneE164ToDigits(parsedAsInternational.number)
  }

  if (parsedWithDefaultCountry?.number) {
    return phoneE164ToDigits(parsedWithDefaultCountry.number)
  }

  return digits
}

export const isPossiblePhoneDigits = (value: string | null | undefined) => {
  const e164 = phoneDigitsToE164(value)
  if (!e164) return false

  const parsed = parsePhoneNumberFromString(e164, phoneMetadata)
  return Boolean(parsed?.isPossible())
}

export const formatPhoneDigitsIntl = (value: string | null | undefined) => {
  const e164 = phoneDigitsToE164(value)
  if (!e164) return ''

  return formatPhoneNumberIntl(e164, phoneMetadata) || e164
}

export const buildWhatsappUrl = (
  value: string | null | undefined,
  message = 'Olá Brasiltec'
) => {
  const digits = normalizePhoneToStoredDigits(value)
  if (!digits) return ''

  const params = new URLSearchParams()
  const normalizedMessage = String(message ?? '').trim()
  if (normalizedMessage) {
    params.set('text', normalizedMessage)
  }

  return `https://wa.me/${digits}${params.size > 0 ? `?${params.toString()}` : ''}`
}

export const formatPhoneForBrazilianInput = (value: string | null | undefined) => {
  const rawValue = String(value ?? '').trim()
  const fallbackDigits = normalizePhoneDigits(rawValue)
  const explicitInternational = rawValue.startsWith('+') || rawValue.startsWith('00')
  const storedDigits = explicitInternational ? normalizePhoneToStoredDigits(rawValue) : ''
  const digits = storedDigits || fallbackDigits
  if (!digits) return ''

  const localDigits = digits.startsWith('55') && (digits.length === 12 || digits.length === 13)
    ? digits.slice(2)
    : digits

  if (localDigits.length <= 2) return localDigits ? `(${localDigits}` : ''
  if (localDigits.length <= 6) return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2)}`
  if (localDigits.length <= 10) {
    return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 6)}-${localDigits.slice(6)}`
  }
  if (localDigits.length === 11) {
    return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 7)}-${localDigits.slice(7, 11)}`
  }

  return formatPhoneDigitsIntl(normalizePhoneToStoredDigits(rawValue) || digits) || rawValue
}

export const resolvePhoneCountry = (
  value: string | null | undefined,
  fallback: PhoneCountryCode = 'BR'
): PhoneCountryCode => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return fallback

  const explicitPhone = parseExplicitInternationalPhone(trimmed)
  if (explicitPhone?.country) {
    return explicitPhone.country
  }

  const digits = normalizePhoneDigits(trimmed)
  if (!digits) return fallback

  const fallbackCallingCode = getCountryCallingCode(fallback, phoneMetadata)
  if (!digits.startsWith(fallbackCallingCode)) {
    return fallback
  }

  const parsedWithFallbackCallingCode = parsePhoneNumberFromString(`+${digits}`, phoneMetadata)
  if (parsedWithFallbackCallingCode?.country) {
    return parsedWithFallbackCallingCode.country
  }

  return fallback
}

export const toInternationalRequestPhone = (value: string | null | undefined) =>
  phoneDigitsToE164(value) ?? ''
